<!-- Maintain_GenderAges.vue -->
<template>
  <!-- Header / actions -->
  <v-toolbar flat density="comfortable" class="mt-4">
    <v-toolbar-title>ADMIN GenderAge Management</v-toolbar-title>
    <v-spacer />
    <v-btn color="primary" prepend-icon="mdi-plus" @click="openModal(false)">
      Add New GenderAge
    </v-btn>
  </v-toolbar>

  <v-alert v-if="error" type="error" variant="tonal" class="mx-4 my-2">
    {{ error }}
  </v-alert>

  <!-- Grouped table -->
  <v-container fluid class="pt-2">
    <v-card rounded="lg" elevation="2">
      <v-data-table
        :headers="headers"
        :items="rows"
        :group-by="[{ key: 'leagueName', order: 'asc' }]"
        :sort-by="[{ key: 'genderAge', order: 'asc' }]"
        item-key="id"
        fixed-header
        height="70vh"
        class="elevation-0 text-left"
        @click:row="onRowClick"
      >
        <!-- Group header with caret (">") -->
        <template #group-header="{ item, columns, toggleGroup, isGroupOpen }">
          <tr class="group-row" @click="toggleGroup(item)" style="cursor:pointer;">
            <td :colspan="columns.length" class="py-2">
              <v-icon
                class="mr-2"
                :class="{ 'rot-90': isGroupOpen(item) }"
              >
                mdi-chevron-right
              </v-icon>
              <span class="ml-2 font-medium">
                {{ item?.value ?? 'Group' }}
              </span>
              <span class="ml-2 text-medium-emphasis">
                ({{ item?.items?.length ?? 0 }})
              </span>
            </td>
          </tr>
        </template>

        <!-- Cells (left-aligned) -->
        <template v-slot:[`item.genderAge`]="{ item }">
          <div class="cell-left">{{ item.genderAge }}</div>
        </template>
        <template v-slot:[`item.gaDescription`]="{ item }">
          <div class="cell-left">{{ item.gaDescription }}</div>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center justify-start ga-1">
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              @click.stop="editGenderAge(item.id)"
            />
            <v-btn
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              @click.stop="deleteGenderAge(item.id)"
            />
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>

  <!-- Add/Edit dialog -->
  <v-dialog v-model="showModal" max-width="560">
    <v-card>
      <v-card-title class="text-h6">
        {{ isEditing ? 'Edit GenderAge' : 'Add New GenderAge' }}
      </v-card-title>
      <v-card-text>
        <v-form ref="formRef" v-model="valid" @submit.prevent="saveGenderAge">
          <div class="text-subtitle-2 mb-2">League</div>
          <v-autocomplete
            v-model="modalLeagueId"
            :items="leagues"
            item-title="LeagueName"
            item-value="id"
            label="Select League"
            clearable
            density="comfortable"
            hide-details="auto"
          />
          <v-text-field
            v-model="modalGenderAge.GenderAge"
            label="GenderAge"
            density="comfortable"
            hide-details="auto"
            class="mt-3"
            :rules="[req]"
          />
          <v-text-field
            v-model="modalGenderAge.GADescription"
            label="Description"
            density="comfortable"
            hide-details="auto"
            class="mt-3"
            :rules="[req]"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="closeModal">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="saveGenderAge">
          {{ isEditing ? 'Update' : 'Save' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { db, genderagesColRef, leaguesColRef } from '@/firebase'
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore'

// ---------- State ----------
const showModal = ref(false)
const isEditing = ref(false)
const saving = ref(false)
const formRef = ref(null)
const valid = ref(false)
const error = ref('')

const genderAges = ref([])   // raw GenderAge docs
const leagues = ref([])      // [{id, League, LeagueName, ...}]
const leaguesById = ref(new Map())
const leaguesByCode = ref(new Map()) // key = League (acronym)

// dialog model
const modalGenderAge = ref({
  id: '',
  LeagueID: '',
  League: '',
  LeagueName: '',
  GenderAge: '',
  GADescription: '',
})
const modalLeagueId = ref('')

// ---------- Table config ----------
const headers = [
  // League column is hidden because we group by it
  { title: 'GenderAge',   key: 'genderAge',    align: 'start', width: 140 },
  { title: 'Description', key: 'gaDescription',align: 'start'  },
  { title: 'Actions',     key: 'actions',      align: 'start', sortable: false, width: 120 },
]

const groupBy = ref([{ key: 'leagueName', order: 'asc' }])
const sortBy  = ref([{ key: 'genderAge', order: 'asc' }])

const req = v => !!(v && String(v).trim()) || 'Required'

function getAdminOrgName () {
  return localStorage.getItem('currentUserOrgName') || ''
}

// Make sure these start as Maps somewhere:
// const leaguesById   = ref(new Map())
// const leaguesByCode = ref(new Map())

const rows = computed(() => {
  const list   = Array.isArray(genderAges.value) ? genderAges.value : []
  const idMap  = leaguesById.value instanceof Map   ? leaguesById.value   : new Map()
  const codeMap= leaguesByCode.value instanceof Map ? leaguesByCode.value : new Map()

  return list.map((ga) => {
    // handle ID vs Id, numeric vs string keys
    const leagueId   = ga.LeagueID ?? ga.LeagueId ?? null
    const leagueCode = ga.League ?? null

    const byId   = leagueId   != null ? idMap.get(String(leagueId))     : undefined
    const byCode = leagueCode ? codeMap.get(String(leagueCode).trim())  : undefined

    const leagueName =
      [byId?.LeagueName, byCode?.LeagueName, ga.LeagueName, leagueCode]
        .find(v => typeof v === 'string' && v.trim()) || 'Unknown'

    return {
      id: ga.id ?? ga.GenderAgeID ?? crypto.randomUUID?.() ?? String(Math.random()),
      leagueName,
      genderAge: ga.GenderAge || '',
      gaDescription: ga.GADescription || '',
    }
  })
})


// ---------- Loaders ----------
async function loadLeagues () {
  leagues.value = []
  leaguesById.value = new Map()
  leaguesByCode.value = new Map()

  const org = getAdminOrgName()
  const qBase = org
    ? query(leaguesColRef, where('Organization', '==', org))
    : query(leaguesColRef)

  const snap = await getDocs(qBase)
  const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))

  list.forEach(l => {
    leaguesById.value.set(l.id, l)
    if (l.League) leaguesByCode.value.set(l.League, l)
  })

  list.sort((a, b) => (a.LeagueName || '').localeCompare(b.LeagueName || ''))
  leagues.value = list
}

async function loadGenderAges () {
  const snap = await getDocs(query(genderagesColRef))
  genderAges.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

// ---------- Dialog control ----------
function openModal (edit = false) {
  isEditing.value = edit
  showModal.value = true
  error.value = ''

  if (!edit) {
    modalGenderAge.value = {
      id: '',
      LeagueID: '',
      League: '',
      LeagueName: '',
      GenderAge: '',
      GADescription: '',
    }
    modalLeagueId.value = ''
  }
}

function closeModal () {
  showModal.value = false
  isEditing.value = false
  modalLeagueId.value = ''
}

function onRowClick (_evt, ctx) {
  if (ctx?.item?.id) editGenderAge(ctx.item.id)
}

// ---------- Actions ----------
function editGenderAge (id) {
  const ga = genderAges.value.find(x => x.id === id)
  if (!ga) return

  isEditing.value = true
  showModal.value = true
  modalGenderAge.value = { ...ga }

  if (ga.LeagueID && leaguesById.value.has(ga.LeagueID)) {
    modalLeagueId.value = ga.LeagueID
  } else if (ga.League && leaguesByCode.value.has(ga.League)) {
    modalLeagueId.value = leaguesByCode.value.get(ga.League).id
  } else {
    modalLeagueId.value = ''
  }
}

async function saveGenderAge () {
  const ok = await formRef.value?.validate()
  if (!ok?.valid) return

  try {
    saving.value = true

    let leaguePayload = { LeagueID: '', League: '', LeagueName: '' }
    if (modalLeagueId.value && leaguesById.value.has(modalLeagueId.value)) {
      const L = leaguesById.value.get(modalLeagueId.value)
      leaguePayload = {
        LeagueID: modalLeagueId.value,
        League: L?.League || '',
        LeagueName: L?.LeagueName || '',
      }
    }

    const payload = {
      ...leaguePayload,
      GenderAge: modalGenderAge.value.GenderAge?.trim() || '',
      GADescription: modalGenderAge.value.GADescription?.trim() || '',
    }

    if (isEditing.value && modalGenderAge.value.id) {
      await updateDoc(doc(db, 'genderages', modalGenderAge.value.id), payload)
    } else {
      const refDoc = await addDoc(genderagesColRef, payload)
      await updateDoc(doc(db, 'genderages', refDoc.id), { GenderAgeID: refDoc.id })
    }

    await loadGenderAges()
    closeModal()
  } catch (e) {
    console.error('saveGenderAge', e)
    error.value = 'Failed to save GenderAge.'
  } finally {
    saving.value = false
  }
}

async function deleteGenderAge (id) {
  if (!confirm('Delete this GenderAge?')) return
  try {
    await deleteDoc(doc(db, 'genderages', id))
    await loadGenderAges()
  } catch (e) {
    console.error('deleteGenderAge', e)
    error.value = 'Failed to delete GenderAge.'
  }
}

// ---------- Init ----------
onMounted(async () => {
  await Promise.all([loadLeagues(), loadGenderAges()])
})
</script>

<style scoped>
/* left align everything */
:deep(.v-data-table th),
:deep(.v-data-table td) { text-align: left !important; }

/* caret rotation */
.rot-90 { transform: rotate(90deg); transition: transform 120ms ease; }

.group-row { background: #f6f7fb; }
.cell-left { text-align: left; }
</style>
